
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Linkedin } from "lucide-react";
import { Dispatch, SetStateAction, ChangeEvent } from "react";

interface TeamMember {
  id: number;
  name: string;
  title: string;
  role: string;
  bio: string;
  image: string;
  contact: string;
  qrCodeImage?: string;
  linkedinUrl?: string;
}

interface TeamMemberCardProps {
  member: TeamMember;
  editingId?: number | null;
  editingField?: string | null;
  editText?: string;
  onEdit?: (member: TeamMember, field: string) => void;
  onSave?: (id: number, field: string) => void;
  onEditText?: Dispatch<SetStateAction<string>>;
  onImageUpload?: (e: ChangeEvent<HTMLInputElement>, id: number) => Promise<void>;
  onQRCodeUpload?: (e: ChangeEvent<HTMLInputElement>, id: number) => Promise<void>;
}

export const TeamMemberCard = ({ member }: TeamMemberCardProps) => {
  return (
    <div className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-center gap-4 flex-shrink-0">
        <Avatar className="h-20 w-20">
          {member.image ? (
            <AvatarImage src={member.image} alt={member.name} />
          ) : (
            <AvatarFallback>
              <User className="h-10 w-10" />
            </AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1">
          <CardTitle className="text-xl">{member.name}</CardTitle>
          <p className="text-sm text-blue-600">{member.title}</p>
          <p className="text-sm text-gray-600">{member.role}</p>
          {member.linkedinUrl && (
            <a 
              href={member.linkedinUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
            >
              <Linkedin className="h-4 w-4" />
              LinkedIn
            </a>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1">
          <p className="text-gray-600">{member.bio}</p>
        </div>
        <div className="mt-4 flex justify-center">
          {member.qrCodeImage && (
            <img 
              src={member.qrCodeImage} 
              alt="QR Code" 
              className="w-32 h-32"
            />
          )}
        </div>
      </CardContent>
    </div>
  );
};
